import React, { useState, useEffect, useMemo } from 'react';
import { Box, ToggleButton, ToggleButtonGroup, useTheme, Tooltip, Chip } from '@mui/material';
import { Help as HelpIcon, CheckCircle, Cancel } from '@mui/icons-material';
import ProgressionTab from '../../../../../theme/ProgressionBuilder/ProgressionTab';
import Text from '../../../../../theme/text/Text';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { tokens } from '../../../../../../theme';
import { setTabErrors } from '../../../../../../newapi/global/progressionTabSlice';
import EMGeneralExplainerBox from '../../../../ModalsPopover/explainers/EMGeneralExplainerBox';
import { 
    getMarketplaceCapabilities, 
    getSyncOptionConfigurations, 
    getDefaultSyncSettings,
    validateSyncConfiguration,
    createSyncStateUpdater
} from '../../utils/etailize/etailizeHelpers';
import cx from 'classnames';
import SHQuickAction from '../../../../../theme/Modal/SHQuickAction';
import Subheading from '../../../../../theme/text/Subheading';

/**
 * EtailizePropagation Component
 * 
 * Handles propagation settings for Etailize integrations.
 * Displays available propagation types (Send/Receive) based on marketplace capabilities
 * and allows users to configure sync options.
 * 
 * Props:
 * - handleDataUpdate: Function to update progression state
 * - handleNext: Function to navigate to next step
 * - handlePrevious: Function to navigate to previous step
 * - data: Current progression data
 * - errors: Validation errors object
 */

// Render advanced sync option toggle with capability awareness
const EtailizeSyncOption = (props) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const isEnabled = props.propagationSettings[props.config.key] === 'true' || props.propagationSettings[props.config.key] === true;
    const hasError = props.syncValidationErrors.some(error => error.field === props.config.key);

    const Newicon = props.config.newIcon;
    return (
        <Box
            sx={{
                width:1,
            }}
        >
            <Box 
                // key={props.config.key}
                sx={{
                    width: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    background:theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[0],
                    border: theme.palette.mode === "dark" ? null : `1px solid ${colors.grey[100]}`,
                    boxShadow:theme.palette.mode === "dark" ? null :  `0px 2px 4px ${colors.grey[200]}`,
                    pr:1,
                    borderRadius:8,
                    gap:2,
                    opacity: props.config.available ? 1 : 0.6
                }}
            >
                {/* Option Info */}
                <Box
                    sx={{
                        py:1,
                        pl:1,
                        display:'flex',
                        flex:1,
                        alignItems:"center",
                        gap:2,
                        overflowX:"hidden"
                    }}
                >
                    <Box
                        sx={{
                            width:32,
                            height:32,
                            position:"relative",
                            background:props.config.available ? colors.green[100] : colors.red[100],
                            color:props.config.available ? colors.green[400] : colors.red[400],
                            display:'flex',
                            alignItems:'center',
                            justifyContent:"center",
                            borderRadius:4,
                            flexShrink:0,
                        }}
                    >
                        <Newicon fontSize="small"/>
                        <SHQuickAction
                            component={
                                <Box sx={{ ml: 0.5 }}>

                                    <Text variant="red" bold>
                                        {props.config.unavailableReason}
                                    </Text>
                                </Box>
                            }
                        >
                            {({anchorEl, onMouseEnter, onMouseLeave, onTouchStart }) => (

                                <Box

                                    aria-owns={anchorEl ? "simple-popover" : undefined}
                                    aria-haspopup="true"
                                    onMouseEnter={!props.config.available? onMouseEnter : null}
                                    onMouseLeave={!props.config.available ? onMouseLeave : null}
                                    onTouchStart={!props.config.available ? onTouchStart : null}
                                    sx={{
                                        position:'absolute',
                                        right:-6,
                                        top:-6,
                                    }}
                                >
                                    {/* <Text sx={{ fontSize: '1.2em' }}>{props.config.icon}</Text> */}
                                    {props.config.available ? (
                                        <CheckCircle sx={{ color: colors.green[400], fontSize: 16 }} />
                                    ) : (
                                        <Cancel sx={{ color: colors.red[400], fontSize: 16 }} />
                                    )}
                                </Box>
                              
                            )}
                        </SHQuickAction>

                    </Box>
                    
                    {/* Label and Description */}
                    <Box 
                    sx={{ 
                        flex: 1,
                        display: "flex", 
                        alignItems: "center", 
                        gap: 1,
                        overflow:"hidden"
                    }}>
                        <Text noBreak fontWeight="medium">{t(props.config.label)}</Text>
                        <Tooltip title={t(props.config.tooltip)} arrow>
                            <HelpIcon sx={{ fontSize: 14, color: theme.palette.mode === "dark" ? colors.txt["secondary"] : colors.grey[400], cursor: 'help' }} />
                        </Tooltip>
                    </Box>
                </Box>
                
                {/* Toggle Control */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <ToggleButtonGroup
                        color={isEnabled ? "success" : "error"}
                        value={isEnabled ? "true" : "false"}
                        exclusive
                        onChange={(e, value) => props.handleChange(props.config.key, value)}
                        disabled={!props.config.available}
                        aria-label={t(props.config.label)}
                        className={cx(`${isEnabled}`, {
                            'active': isEnabled
                        })}
                        sx={{
                            background: colors.red[100],
                            padding: 0.5,
                            borderRadius: 5,
                            transition: "250ms",
                            "&.active": {
                                background: colors.green[100],
                                transition: "250ms",
                            },
                            ...(hasError && {
                                border: `2px solid ${colors.red[400]}`
                            })
                        }}
                    >
                        <ToggleButton 
                            sx={{
                                padding: 0,
                                paddingX: 1.5,
                                border: 0,
                                lineHeight: "28px",
                                textTransform: "unset",
                                borderRadius: "28px !important",
                                color: colors.green[400],
                                transition: "250ms",
                                fontSize: '0.8em',
                                "&.Mui-selected,&.Mui-selected:hover": {
                                    background: colors.red[400],
                                    color: `${theme.palette.mode === "dark" ? "rgba(0,0,0,.75)" : "#fff"}`
                                }
                            }}
                            value="false"
                        >
                            {t('inactive')}
                        </ToggleButton>
                        <ToggleButton 
                            sx={{
                                padding: 0,
                                paddingX: 1.5,
                                border: 0,
                                lineHeight: "28px",
                                textTransform: "unset",
                                borderRadius: "28px !important",
                                color: colors.red[400],
                                transition: "250ms",
                                fontSize: '0.8em',
                                "&.Mui-selected,&.Mui-selected:hover": {
                                    background: colors.green[400],
                                    color: `${theme.palette.mode === "dark" ? "rgba(0,0,0,.75)" : "#fff"}`
                                }
                            }}
                            value="true"
                        >
                            {t('active')}
                        </ToggleButton>
                    </ToggleButtonGroup>
                </Box>
            </Box>

            <Text light={theme.palette.mode === "dark"}  fontSize={13} sx={{ mt: 1,ml:2, }}>
                {t(props.config.description)}
            </Text>
        </Box>
    );
};
const Content = (props) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const colors = tokens(theme.palette.mode);
    const data = { ...props.data };
    const dispatch = useDispatch();

    // Get selected marketplace from progression data
    const selectedMarketplace = props.data?.etailize_marketplace;
    
    // Memoize marketplace capabilities to prevent infinite loops
    const capabilities = useMemo(() => {
        return getMarketplaceCapabilities(selectedMarketplace);
    }, [selectedMarketplace]);
    
    // Memoize sync configurations to prevent recalculation
    const syncConfigurations = useMemo(() => {
        return getSyncOptionConfigurations(capabilities, t);
    }, [capabilities, t]);
    
    // Initialize propagation settings based on marketplace capabilities
    const [propagationSettings, setPropagationSettings] = useState(() => {
        const defaults = getDefaultSyncSettings(capabilities);
        return {
            ...defaults,
            syncOrders: data.syncOrders ?? defaults.syncOrders ?? 'true',
            syncStock: data.syncStock ?? defaults.syncStock ?? 'true',
            syncFulfillment: data.syncFulfillment ?? defaults.syncFulfillment ?? 'false'
        };
    });

    // Create state updater that validates against capabilities
    const syncStateUpdater = createSyncStateUpdater(setPropagationSettings, capabilities);
    
    // Validation errors for sync configuration
    const [syncValidationErrors, setSyncValidationErrors] = useState([]);

    useEffect(() => {
        // Validate sync configuration when settings change
        const errors = validateSyncConfiguration(propagationSettings, capabilities, t);
        setSyncValidationErrors(errors);
        
        // Update parent component data
        props.handleDataUpdate(propagationSettings);
    }, [propagationSettings, capabilities, t]);

    const handleChange = (key, val) => {
        const config = syncConfigurations.find(c => c.key === key);
        
        // Prevent enabling unsupported sync options
        if (val === 'true' && config && !config.available) {
            // Show error message or warning
            console.warn(`Cannot enable ${key}: not supported by marketplace`);
            return;
        }
        
        syncStateUpdater(key, val);
        dispatch(setTabErrors({ ...props.errors, [key]: '' }));
    };


    // // Render advanced sync option toggle with capability awareness
    // const renderSyncOption = (config) => {
    //     const isEnabled = propagationSettings[config.key] === 'true' || propagationSettings[config.key] === true;
    //     const hasError = syncValidationErrors.some(error => error.field === config.key);

    //     return (
    //         <Box 
    //             key={config.key}
    //             sx={{
    //                 width: 1,
    //                 display: "flex",
    //                 alignItems: "center",
    //                 justifyContent: "space-between",
    //                 gap: 2,
    //                 mb: 2,
    //                 p: 2,
    //                 border: `1px solid ${config.available ? colors.grey[200] : colors.grey[300]}`,
    //                 borderRadius: 2,
    //                 backgroundColor: config.available ? 'transparent' : colors.grey[50],
    //                 opacity: config.available ? 1 : 0.6
    //             }}
    //         >
    //             {/* Option Info */}
    //             <Box sx={{ flex: 1, display: "flex", alignItems: "center", gap: 1.5 }}>
    //                 {/* Icon and Status */}
    //                 <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
    //                     <Text sx={{ fontSize: '1.2em' }}>{config.icon}</Text>
    //                     {config.available ? (
    //                         <CheckCircle sx={{ color: colors.green[400], fontSize: 16 }} />
    //                     ) : (
    //                         <Cancel sx={{ color: colors.red[400], fontSize: 16 }} />
    //                     )}
    //                 </Box>
                    
    //                 {/* Label and Description */}
    //                 <Box sx={{ flex: 1 }}>
    //                     <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
    //                         <Text fontWeight="medium">{t(config.label)}</Text>
    //                         {/* <Chip 
    //                             label={t(config.category === 'send' ? 'send_to_marketplace' : 'receive_from_marketplace')} 
    //                             size="small"
    //                             variant="outlined"
    //                             sx={{ 
    //                                 fontSize: '0.7em',
    //                                 height: 20,
    //                                 color: config.category === 'send' ? colors.blue[600] : colors.green[600],
    //                                 borderColor: config.category === 'send' ? colors.blue[300] : colors.green[300]
    //                             }}
    //                         /> */}
    //                         <Tooltip title={t(config.tooltip)} arrow>
    //                             <HelpIcon sx={{ fontSize: 14, color: colors.grey[400], cursor: 'help' }} />
    //                         </Tooltip>
    //                     </Box>
    //                     <Text fontSize={12} color="textSecondary" sx={{ mt: 0.5 }}>
    //                         {t(config.description)}
    //                     </Text>
    //                     {!config.available && (
    //                         <Text fontSize={11} color="error" sx={{ mt: 0.5, display: 'block' }}>
    //                             {config.unavailableReason}
    //                         </Text>
    //                     )}
    //                 </Box>
    //             </Box>
                
    //             {/* Toggle Control */}
    //             <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
    //                 <ToggleButtonGroup
    //                     color={isEnabled ? "success" : "error"}
    //                     value={isEnabled ? "true" : "false"}
    //                     exclusive
    //                     onChange={(e, value) => handleChange(config.key, value)}
    //                     disabled={!config.available}
    //                     aria-label={t(config.label)}
    //                     className={cx(`${isEnabled}`, {
    //                         'active': isEnabled
    //                     })}
    //                     sx={{
    //                         background: colors.red[100],
    //                         padding: 0.5,
    //                         borderRadius: 5,
    //                         transition: "250ms",
    //                         "&.active": {
    //                             background: colors.green[100],
    //                             transition: "250ms",
    //                         },
    //                         ...(hasError && {
    //                             border: `2px solid ${colors.red[400]}`
    //                         })
    //                     }}
    //                 >
    //                     <ToggleButton 
    //                         sx={{
    //                             padding: 0,
    //                             paddingX: 1.5,
    //                             border: 0,
    //                             lineHeight: "28px",
    //                             textTransform: "unset",
    //                             borderRadius: "28px !important",
    //                             color: colors.green[400],
    //                             transition: "250ms",
    //                             fontSize: '0.8em',
    //                             "&.Mui-selected,&.Mui-selected:hover": {
    //                                 background: colors.red[400],
    //                                 color: `${theme.palette.mode === "dark" ? "rgba(0,0,0,.75)" : "#fff"}`
    //                             }
    //                         }}
    //                         value="false"
    //                     >
    //                         {t('inactive')}
    //                     </ToggleButton>
    //                     <ToggleButton 
    //                         sx={{
    //                             padding: 0,
    //                             paddingX: 1.5,
    //                             border: 0,
    //                             lineHeight: "28px",
    //                             textTransform: "unset",
    //                             borderRadius: "28px !important",
    //                             color: colors.red[400],
    //                             transition: "250ms",
    //                             fontSize: '0.8em',
    //                             "&.Mui-selected,&.Mui-selected:hover": {
    //                                 background: colors.green[400],
    //                                 color: `${theme.palette.mode === "dark" ? "rgba(0,0,0,.75)" : "#fff"}`
    //                             }
    //                         }}
    //                         value="true"
    //                     >
    //                         {t('active')}
    //                     </ToggleButton>
    //                 </ToggleButtonGroup>
    //             </Box>
    //         </Box>
    //     );
    // };

    return (
         //<Box>
        //     {/* Information Box */}
        //     <Box sx={{ 
        //         p: 2.5, 
        //         backgroundColor: colors.blue[50],
        //         borderRadius: 2,
        //         border: `1px solid ${colors.blue[200]}`,
        //         display: "flex",
        //         flexDirection: "column"
        //     }}>
        //         <Text fontWeight="bold" sx={{ mb: 1, color: colors.blue[700] }}>
        //             💡 {t('sync_configuration_info')}
        //         </Text>
        //         <Text fontSize={12} color="textSecondary" sx={{ mb: 1, ml:3 }}>
        //             {t('sync_configuration_info_description')}
        //         </Text>
        //         <Text fontSize={12} color="textSecondary" sx={{ml:3 }}>
        //             {t('sync_settings_can_be_changed_later')}
        //         </Text>
        //     </Box>
        //     <br/>

        <Box>
            {/* Sync Configuration Options */}
            <Box sx={{ mb: 3 }}>
                {/* <Text fontSize={18} fontWeight="bold" sx={{ mb: 2 }}>
                    {t('sync_configuration')}
                </Text> */}
                
                {/* Group by category */}
                {['send', 'receive'].map(category => {
                    const categoryConfigs = syncConfigurations.filter(config => config.category === category);
                    
                    if (categoryConfigs.length === 0) return null;
                    
                    return (
                        <Box key={category} sx={{mb:4,}}>
                            <Subheading
                                sx={{
                                    mb:1,
                                }}
                            >
                                {t(category === 'send' ? 'send_to_marketplace' : 'receive_from_marketplace')}
                            </Subheading>
                            
                            <Box
                                sx={{
                                    display:"flex",
                                    flexDirection:'column',
                                    gap:2,
                                }}
                            >
                                {categoryConfigs.map((config,index) => (
                                    
                                    <EtailizeSyncOption 
                                        key={index+"so"}
                                        config={config}
                                        propagationSettings={propagationSettings}
                                        handleChange={handleChange}
                                        // handleSyncToggle={handleSyncToggle}
                                        // hasModifyPermission={hasModifyPermission}
                                        syncValidationErrors={syncValidationErrors}
                                    />
                                ))}
                            </Box>
                        </Box>
                        // <Box key={category} sx={{ mb: 3 }}>
                        //     <Text fontSize={18} fontWeight="bold" sx={{ 
                        //         mb: 2,
                        //         color: category === 'send' ? colors.blue[600] : colors.green[600],
                        //         display: 'flex',
                        //         alignItems: 'center',
                        //         gap: 1
                        //     }}>
                        //         {t(category === 'send' ? 'send_to_marketplace' : 'receive_from_marketplace')}
                        //     </Text>
                            
                        //     {categoryConfigs.map(config => renderSyncOption(config))}
                        // </Box>
                    );
                })}
            </Box>

            {/* Validation Errors */}
            {syncValidationErrors.length > 0 && (
                <Box sx={{ 
                }}>
                    <Text bold  sx={{ mb: 1 }}>
                            {t('configuration_errors')}
                    </Text>
                    {syncValidationErrors.map((error, index) => (
                        <Box
                            key={index+"sve"}
                            sx={{
                                background:colors.red[100],
                                px:2,
                                borderRadius:4,
                                py:0.5,
                            }}
                        >
                            <Text  variant={"red"}>
                                {error.message}
                            </Text>
                        </Box>
                    ))}
                </Box>
            )}

            

        </Box>
    );
};

const EtailizePropagation = (props) => {
    const { t } = useTranslation();

    return (
        <ProgressionTab 
            tabId="tabEtailizePropagation" 
            subtitle={t('propagation_settings')}
            step={3} 
            maxSteps={5}
            nextTab="tabEtailizeCredentials"
        >
            <Content {...props} />
        </ProgressionTab>
    );
};

export default EtailizePropagation;